
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { Debounce, cancelDebounce } from '@/utils/decorators';
import { BULK_EDIT_PAGE_SIZE } from '@/utils/constants';
import { toErrorMessage } from '@/utils/errorHandling';
import Toast from '@/utils/toast';
import TableHeaders from '@/components/shared/suites/TableHeaders.vue';
import PaginationControls from '@/components/shared/suites/PaginationControls.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import TextField from '@/components/shared/TextField.vue';
import TagPreview from '@/components/shared/TagPreview.vue';
import SelectLocationsModal from '@/components/multi-location/SelectLocationsModal.vue';
import BulkEditProgress from '@/components/multi-location/BulkEditProgress.vue';
import ActionHistoryModal from '@/components/multi-location/ActionHistoryModal.vue';
import BulkCreateTagModal from '@/components/multi-location/modals/BulkCreateTagModal.vue';

const namespace: string = 'bulkEdit';
const jobNamespace: string = 'bulkTagCreate';

@Component<BulkEditSettings>({
	components: {
		TableHeaders,
		PaginationControls,
		TextButton,
		TextField,
		TagPreview,
		SelectLocationsModal,
		BulkEditProgress,
		ActionHistoryModal,
		BulkCreateTagModal
	}
})
export default class BulkEditSettings extends Vue {
	@Action('fetchLocationRows', { namespace }) private fetchLocationRows!: (params: BulkLocationQueryParams) => Promise<void>;
	@Action('setLocationIds', { namespace }) private setSelectedLocationIds!: (locationIds: string[]) => void;
	@Action('fetchActiveJob', { namespace: jobNamespace }) private fetchActiveJob!: () => Promise<void>;
	@Action('fetchViewedJob', { namespace: jobNamespace }) private fetchViewedJob!: (jobId: string) => Promise<void>;
	@Action('fetchJobHistory', { namespace: jobNamespace }) private fetchJobHistory!: (params?: TableFilterParams) => Promise<void>;
	@Action('cancelJob', { namespace: jobNamespace }) private cancelJob!: (jobId: string) => Promise<void>;
	@Action('stopPolling', { namespace: jobNamespace }) private stopPolling!: () => void;
	@Action('clearViewedJob', { namespace: jobNamespace }) private clearViewedJob!: () => void;
	@Getter('getLocationRows', { namespace }) private locationRows!: BulkLocationRow[];
	@Getter('getCount', { namespace }) private count!: number;
	@Getter('getSelectedLocationIds', { namespace }) private selectedLocationIds!: string[];
	@Getter('getRestaurants', { namespace: 'auth' }) private restaurants!: Tenant[] | null;
	@Getter('getActiveJob', { namespace: jobNamespace }) private activeJob!: BulkJob | null;
	@Getter('getViewedJob', { namespace: jobNamespace }) private viewedJob!: BulkJob | null;
	@Getter('hasActiveJob', { namespace: jobNamespace }) private isJobRunning!: boolean;
	@Getter('getJobHistory', { namespace: jobNamespace }) private jobHistory!: BulkJob[];
	@Getter('getJobHistoryCount', { namespace: jobNamespace }) private jobHistoryCount!: number;
	@Getter('getJobHistoryFilters', { namespace: jobNamespace }) private historyFilters!: TableFilterParams;

	// A running job clearing out is the signal the tag was created: refresh the table so the new tag
	// shows in the Tags column. Deliberately not `viewedJob` — that also fills in when a historical job
	// is opened from the action history, which would reload the table out from under the operator.
	@Watch('activeJob')
	private onJobFinished(job: BulkJob | null, previousJob: BulkJob | null): void {
		if (!previousJob || job) {
			return;
		}
		this.loadRows();
	}

	private readonly itemsPerPage: number = BULK_EDIT_PAGE_SIZE;
	private currentPage: number = 1;
	private searchInput: string = '';
	private sortBy: string = 'location_name';
	private sortDirection: 'asc' | 'desc' = 'asc';
	private locationIds: string[] = [];
	private selectedLocationRowIds: string[] = [];
	private isFetching: boolean = true;
	private isLoadingHistory: boolean = false;
	private showLocationsModal: boolean = false;
	private showCreateTagModal: boolean = false;
	private showHistoryModal: boolean = false;

	private get displayedJob(): BulkJob | null {
		return this.activeJob ?? this.viewedJob;
	}

	private get totalPages(): number {
		return Math.max(1, Math.ceil(this.count / this.itemsPerPage));
	}

	private get totalHistoryPages(): number {
		const limit: number = this.historyFilters.limit || 10;
		return Math.max(1, Math.ceil(this.jobHistoryCount / limit));
	}

	private get tableHeaders(): TableHeader[] {
		const selectedCount: number = this.selectedLocationRowIds.length;
		return [
			{ text: '', name: 'select-all', class: 'checkbox-col' },
			{
				text: this.$t('multi_location_settings.bulk_edit_settings.table.location') + (selectedCount ? ` (${selectedCount})` : ''),
				name: 'location_name',
				sort: this.sortDirection
			},
			{ text: this.$t('multi_location_settings.bulk_edit_settings.table.group_name'), name: 'group_name', sort: this.sortDirection },
			{ text: this.$t('multi_location_settings.bulk_edit_settings.table.slug'), name: 'slug', sort: this.sortDirection },
			{ text: this.$t('multi_location_settings.bulk_edit_settings.table.tags') }
		];
	}

	private get currentPageSelectedCount(): number {
		return this.locationRows.filter(row => this.selectedLocationRowIds.includes(row.location_id)).length;
	}

	private get isCurrentPageFullySelected(): boolean {
		return !!this.locationRows.length && this.currentPageSelectedCount === this.locationRows.length;
	}

	private get isCurrentPagePartiallySelected(): boolean {
		return !!this.currentPageSelectedCount && this.currentPageSelectedCount < this.locationRows.length;
	}

	// Forces the header checkbox to re-mount so it can't get stuck showing a stale state
	private get headerCheckboxState(): 'unchecked' | 'indeterminate' | 'checked' {
		if (this.isCurrentPageFullySelected) {
			return 'checked';
		}
		if (this.isCurrentPagePartiallySelected) {
			return 'indeterminate';
		}
		return 'unchecked';
	}

	private async created(): Promise<void> {
		// Bulk editing location settings is meaningless with a single location.
		if ((this.restaurants?.length ?? 0) <= 1) {
			this.$router.replace('/multi-location-settings').catch(() => {});
			return;
		}

		this.locationIds = this.selectedLocationIds;

		if (!this.locationIds.length) {
			this.$router.replace('/multi-location-settings').catch(() => {});
			return;
		}

		await this.loadRows();

		try {
			await this.fetchActiveJob();
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
	}

	private beforeDestroy(): void {
		cancelDebounce(this.debouncedSearch);
		this.stopPolling();
		this.clearViewedJob();
	}

	/**
	 * Fetch a page of the selected locations.
	 *
	 * @return {Promise<void>}
	 */
	private async loadRows(): Promise<void> {
		this.isFetching = true;
		try {
			await this.fetchLocationRows({
				location_ids: this.locationIds,
				page: this.currentPage,
				limit: this.itemsPerPage,
				search: this.searchInput || undefined,
				sortBy: this.sortBy,
				sortDirection: this.sortDirection
			});
		}
		catch (error) {
			Toast.error(this.$t('multi_location_settings.bulk_edit_settings.error_fetching', { errorMessage: toErrorMessage(error) }));
		}
		finally {
			this.isFetching = false;
		}
	}

	private goToPreviousPage(): void {
		if (this.currentPage > 1) {
			this.currentPage--;
			this.loadRows();
		}
	}

	private goToNextPage(): void {
		if (this.currentPage < this.totalPages) {
			this.currentPage++;
			this.loadRows();
		}
	}

	/**
	 * Sort by a column, flipping the direction when the same column is clicked again.
	 *
	 * @param {string} columnName
	 * @return {void}
	 */
	private onToggleSort(columnName: string): void {
		if (!columnName || columnName === 'select-all') {
			return;
		}
		if (this.sortBy === columnName) {
			this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		}
		else {
			this.sortBy = columnName;
			this.sortDirection = 'asc';
		}
		this.currentPage = 1;
		this.loadRows();
	}

	@Debounce(500)
	private debouncedSearch(): void {
		this.currentPage = 1;
		this.loadRows();
	}

	private clearSearch(): void {
		this.searchInput = '';
		this.currentPage = 1;
		this.loadRows();
	}

	/**
	 * Changing the selected locations changes the whole page context, so persist the new set and reload
	 * the table and the selection from scratch.
	 *
	 * @param {string[]} locationIds
	 * @return {void}
	 */
	private onLocationsChanged(locationIds: string[]): void {
		this.showLocationsModal = false;
		this.locationIds = locationIds;
		this.selectedLocationRowIds = [];
		this.currentPage = 1;
		this.setSelectedLocationIds(locationIds);
		this.loadRows();
	}

	/**
	 * The selection has been handed to the job, so it is spent whether or not the job succeeds.
	 *
	 * @return {void}
	 */
	private onTagCreateStarted(): void {
		this.showCreateTagModal = false;
		this.selectedLocationRowIds = [];
	}

	/**
	 * @param {string} jobId
	 * @return {Promise<void>}
	 */
	private async onCancelJob(jobId: string): Promise<void> {
		try {
			await this.cancelJob(jobId);
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
	}

	/**
	 * Open the action history on a freshly loaded first page.
	 *
	 * @return {Promise<void>}
	 */
	private async openHistory(): Promise<void> {
		this.showHistoryModal = true;
		await this.applyHistoryFilters({ ...this.historyFilters, page: 1 });
	}

	/**
	 * @param {TableFilterParams} next
	 * @return {Promise<void>}
	 */
	private async applyHistoryFilters(next: TableFilterParams): Promise<void> {
		this.isLoadingHistory = true;
		try {
			await this.fetchJobHistory(next);
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
		finally {
			this.isLoadingHistory = false;
		}
	}

	private async goToPreviousHistoryPage(): Promise<void> {
		const current: number = this.historyFilters.page || 1;
		if (current > 1) {
			await this.applyHistoryFilters({ ...this.historyFilters, page: current - 1 });
		}
	}

	private async goToNextHistoryPage(): Promise<void> {
		const current: number = this.historyFilters.page || 1;
		if (current < this.totalHistoryPages) {
			await this.applyHistoryFilters({ ...this.historyFilters, page: current + 1 });
		}
	}

	/**
	 * @param {string} columnName
	 * @return {Promise<void>}
	 */
	private async onHistorySortChange(columnName: string): Promise<void> {
		const nextDirection: 'asc' | 'desc' = this.historyFilters.sortBy === columnName
			? (this.historyFilters.sortDirection === 'asc' ? 'desc' : 'asc')
			: 'asc';
		await this.applyHistoryFilters({
			...this.historyFilters,
			page: 1,
			sortBy: columnName,
			sortDirection: nextDirection
		});
	}

	/**
	 * Open a historical job in the progress card. The history rows carry counts only, so seeing a
	 * job's per-location breakdown means loading its detail and handing the view back to the card.
	 *
	 * @param {string} jobId
	 * @return {Promise<void>}
	 */
	private async viewHistoryJob(jobId: string): Promise<void> {
		try {
			await this.fetchViewedJob(jobId);
			// Only close once there is something to show — otherwise the modal would vanish and
			// reveal nothing behind it.
			if (this.viewedJob) {
				this.showHistoryModal = false;
				window.scrollTo({ top: 0, behavior: 'smooth' });
			}
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
	}

	/**
	 * @param {string} locationId
	 * @return {boolean}
	 */
	private isLocationSelected(locationId: string): boolean {
		return this.selectedLocationRowIds.includes(locationId);
	}

	/**
	 * Add or remove a location row from the selection.
	 *
	 * @param {string} locationId
	 * @return {void}
	 */
	private toggleLocationSelection(locationId: string): void {
		const index: number = this.selectedLocationRowIds.indexOf(locationId);
		if (index >= 0) {
			this.selectedLocationRowIds.splice(index, 1);
			return;
		}

		this.selectedLocationRowIds.push(locationId);
	}

	/**
	 * Select every row on the current page, or clear the page's rows when any of them are selected.
	 * Selections on other pages are left alone.
	 *
	 * @return {void}
	 */
	private toggleCurrentPageSelection(): void {
		const currentPageIds: string[] = this.locationRows.map(row => row.location_id);

		if (this.currentPageSelectedCount > 0) {
			this.selectedLocationRowIds = this.selectedLocationRowIds.filter(id => !currentPageIds.includes(id));
			return;
		}

		this.selectedLocationRowIds = [...this.selectedLocationRowIds, ...currentPageIds];
	}
}
